import { useCallback, useEffect, useMemo } from "react";

import { selectGeneratedTheme } from "legacy/selectors/themeSelectors";
import DropDownComponent from "legacy/widgets/DropdownWidget/DropdownComponent";
import { DEFAULT_ICON_SIZE } from "legacy/widgets/shared";
import { useAppSelector } from "store/helpers";
import { DropdownCellWrapper, extractDropdownOptions } from "./Shared";
import type { CompactMode, EditProps } from "../Constants";
import type { Position } from "./Shared";
import type { TableCellProps } from "../TableUtilities";

const EditSingleSelectCell = (props: {
  editProps: EditProps;
  value: any;
  inputPosition: Position;
  compactMode: CompactMode;
  cellProps: TableCellProps;
}) => {
  const { value, editProps, inputPosition } = props;
  const {
    handleEditStop,
    editIsRequired,
    editOptions = [],
    transformation,
    onDropdownSearchTextChanged,
    editDropdownClientSideFiltering,
    currentEditDropdownSearchText,
    dropdownOptionsLoading,
    allowClearing,
  } = editProps;
  const transformedEditOptions = useMemo(() => {
    return extractDropdownOptions(editOptions, transformation);
  }, [editOptions, transformation]);

  const selectedIndex = useMemo(() => {
    return transformedEditOptions.findIndex((opt) => opt.value === value);
  }, [value, transformedEditOptions]);

  const handleValueChange = useCallback(
    (val: any) => {
      handleEditStop({
        shouldSave: true,
        value: val?.value,
        validationErrors: [],
        dropdownOption: {
          ...val,
          value: val?.value ? JSON.stringify(val?.value) : "",
        },
      });
    },
    [handleEditStop],
  );
  const handleQueryChanged = useCallback(
    (query: string) => {
      onDropdownSearchTextChanged && onDropdownSearchTextChanged(query);
    },
    [onDropdownSearchTextChanged],
  );
  useEffect(() => {
    return () => {
      if (editDropdownClientSideFiltering) {
        // stop editing w/o saving when component unmounts
        // Note that we don't do this when client side filtering is disabled because
        // the component is usually _expected_ to remount when the enclosing table
        // reloads.
        handleEditStop({ shouldSave: false });
      }
    };
    // not necessary because the input will no longer be rendered once editing stops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDropdownClientSideFiltering]);

  const generatedTheme = useAppSelector(selectGeneratedTheme);

  return (
    <DropdownCellWrapper
      $compactMode={props.compactMode}
      cellProperties={props.cellProps.cellProperties}
      $position={inputPosition}
      clearable={allowClearing}
    >
      <DropDownComponent
        selectionType="SINGLE_SELECT"
        onOptionSelected={handleValueChange}
        selectedIndex={selectedIndex}
        onSearchTextChange={handleQueryChanged}
        clientSideFiltering={editDropdownClientSideFiltering ?? true}
        query={currentEditDropdownSearchText}
        isLoading={dropdownOptionsLoading ?? false}
        options={transformedEditOptions}
        isRequired={editIsRequired}
        widgetId="unused"
        allowClearing={allowClearing}
        forceOpen={true}
        onDropdownClosed={() => handleEditStop({ shouldSave: false })}
        width={inputPosition?.width}
        vertical={true}
        caretIconColor={generatedTheme.dropdown.caratIcon}
        iconSize={DEFAULT_ICON_SIZE}
      />
    </DropdownCellWrapper>
  );
};

export default EditSingleSelectCell;
